/**
 * Created by rastwe on 1/26/2017.
 */

import axios from 'axios';
import envConfig from '@ey/voltron-config';
import {HELIX_PROJECT_FIT, HelixAttributesUrlTags, HelixReportKeys} from './uiconstants';
import _ from 'lodash';

const {
	CurrencyCodeTag,
	CurrencyTypeTag,
	AnalysisYearToDateTag,
	ComparativeYearToDateTag,
	ComparativeQtr,
	ComparativePeriod,
	AnalysisQtr,
	AnalysisPeriod,
	BusinessUnitTag,
	SeparatorTag,
	AmpersandTag,
	EYAccountTag,
	BUTag
} = HelixAttributesUrlTags;

const {FSC, UIC, OSJE} = HelixReportKeys;

let returnUrl = undefined;

class Env extends Object {
	/**
	 * Returns true if the environment is production.
	 * @return {boolean} is production
	 */
	static isProd() {
		const env = process.env || 'development';
		return env.NODE_ENV !== 'development';
	}

	static setReturnUrl(url) {
		returnUrl = url;
	}

	static getReturnUrl() {
		return returnUrl;
	}

	static getFederatedHostName() {
		return FEDERATED_HOST_NAME;
	}

	/**
	 * Returns the current environment's server URL, appended with
	 * whatever path is passed in as an argument, or just the root
	 * serverURL if path is undefined.
	 * Notice that I'm requiring that all paths be preceeded by a "/"
	 * character. This is because you want any request to the API
	 * root to look like "/", instead of "".
	 * @param {String} path - Path to be appended to the server URL
	 * @return {String} complete path string
	 */
	static getServerUrl(path = '') {
		return `${envConfig.url.serviceUrl}${path}`;
	}

	static getWebUrl(path = '') {
		return `${envConfig.url.hercules_web}/${path}`;
	}

	static getDocumentUrl(documentId, engagementId, parentName) {
		let path = `dashboard?category=documents&engagementId=${engagementId}`;
		if (documentId) {
			path += `&filterdocumentid=${documentId}`;
		}
		if (parentName) {
			path += `&view=${parentName}`;
		}

		const webUrl = `${this.getWebUrl(path + '&viewtype=documents#engagementOverview')}`;
		return webUrl;
	}

	static getGuidanceReportKey(guidanceUrl) {
		let reportKey = guidanceUrl;
		return reportKey;
	}

	static getHelixPrimaryAnalyzer(helixData, analyzerType) {
		if (analyzerType) {
			if (helixData && helixData?.length > 0) {
				let primaryAnalyzer = helixData[1];
				if (primaryAnalyzer) {
					return primaryAnalyzer?.helixAnalyzerType?.toLowerCase() === analyzerType.toLowerCase()
						? primaryAnalyzer
						: false;
				}
			}
		}

		return false;
	}

	static getHelixChartUrl(helixData, guidanceUrl, accountId, entityBU) {
		if (!helixData || helixData.length === 0 || !helixData[1]) {
			return undefined;
		}

		if (!guidanceUrl) {
			return undefined;
		}

		const guidanceUrlParts = guidanceUrl.split('/');
		const analyzerType = guidanceUrlParts[0];
		const reportKey = guidanceUrlParts[1];
		const componentKey = guidanceUrlParts[2].split('?')[0];
		let extraKey;
		const accountKey = accountId;

		switch (reportKey) {
			case OSJE:
				extraKey = `ViewBy=${encodeURI('Analysis period')}`;
				break;
			case UIC:
			case FSC:
				if (accountId && reportKey === FSC) {
					extraKey = `View=${encodeURI('Activity comparison')}`;
				} else {
					extraKey = guidanceUrlParts[2].split('?')[1];
				}
				break;
			default:
				extraKey = '';
		}

		const primaryAnalyzer = this.getHelixPrimaryAnalyzer(helixData, analyzerType);
		if (!primaryAnalyzer) {
			return undefined;
		}

		let url = `${HELIX_PROJECT_FIT}${primaryAnalyzer.projectId}/${primaryAnalyzer.helixAnalyzerId}`;
		url += `/${reportKey}/${componentKey}?`;
		url += this.getHelixChartAttributes(
			primaryAnalyzer.helixJsonProjectAttributes,
			accountKey,
			extraKey,
			reportKey,
			entityBU
		);

		console.log('original helix url:', url);
		return url;
	}

	static getHelixChartFilters(helixData, guidanceUrl, accountId, entityBU) {
		if (!helixData || helixData.length === 0 || !guidanceUrl) {
			return undefined;
		}

		const extraFilters = new Map();
		const guidanceUrlParts = guidanceUrl.split('/');
		const analyzerType = guidanceUrlParts[0];
		const reportKey = guidanceUrlParts[1];
		const componentKey = guidanceUrlParts[2].split('?')[0];
		const accountKey = accountId;

		switch (reportKey) {
			case OSJE:
				extraFilters.set('ViewBy', 'Analysis period');
				break;
			case UIC:
			case FSC:
				if (accountId && reportKey === FSC) {
					extraFilters.set('View', 'Activity comparison');
				} else {
					const queryParam = guidanceUrlParts[2].split('?')[1];
					const [key, value] = queryParam.split('=');
					extraFilters.set(key, value);
				}
				break;
		}

		const primaryAnalyzer = this.getHelixPrimaryAnalyzer(helixData, analyzerType);
		if (!primaryAnalyzer) {
			return undefined;
		}

		const filterAttributes = this.getHelixChartFilterAttributes(
			primaryAnalyzer.helixProjectAttributesReadModel,
			accountKey,
			extraFilters,
			entityBU
		);

		console.log('helixVisualFilters:', {
			reportKey,
			componentKey,
			filters: filterAttributes,
			analyzer: primaryAnalyzer
		});

		return {
			reportKey,
			componentKey,
			filters: filterAttributes,
			analyzer: primaryAnalyzer
		};
	}

	static getBusinessUnitsUrl(businessUnits) {
		let businessUnitUrl = '';
		if (businessUnits && businessUnits.length > 0) {
			businessUnitUrl += BusinessUnitTag;
			businessUnits.forEach((bu) => {
				businessUnitUrl += bu + SeparatorTag;
			});
		}
		if (businessUnitUrl.length > 0 && businessUnitUrl.charAt(businessUnitUrl.length - 1) === '~') {
			businessUnitUrl = businessUnitUrl.substring(0, businessUnitUrl.length - 3);
		}
		return businessUnitUrl;
	}

	static getHelixChartAttributes(helixProjectAttributes, accountKey, extraKey, reportKey, entityBU) {
		let helixProjectsAttributesUrl = '';
		if (helixProjectAttributes) {
			if (helixProjectAttributes?.currencyCode) {
				helixProjectsAttributesUrl += CurrencyCodeTag + helixProjectAttributes.currencyCode;
			}
			if (helixProjectAttributes?.currencyType) {
				helixProjectsAttributesUrl += AmpersandTag + CurrencyTypeTag + helixProjectAttributes.currencyType;
			}
			if (helixProjectAttributes?.analysisPeriod?.yearToDateQuery) {
				helixProjectsAttributesUrl +=
					AmpersandTag + AnalysisYearToDateTag + helixProjectAttributes.analysisPeriod.yearToDateQuery;
			}
			if (helixProjectAttributes?.comparativePeriod?.yearToDateQuery) {
				helixProjectsAttributesUrl +=
					AmpersandTag + ComparativeYearToDateTag + helixProjectAttributes.comparativePeriod?.yearToDateQuery;
			}
			if (
				(helixProjectAttributes?.isAllBusinessUnitSelected === false && helixProjectAttributes?.businessUnit) ||
				entityBU
			) {
				let businessUnit = '';
				if (entityBU) {
					businessUnit = this.getBusinessUnitsUrl(entityBU);
				} else {
					businessUnit = this.getBusinessUnitsUrl(helixProjectAttributes.businessUnit);
				}
				if (businessUnit.length > 0) {
					helixProjectsAttributesUrl += AmpersandTag + businessUnit;
				}
			}
			if (accountKey) {
				helixProjectsAttributesUrl += AmpersandTag + EYAccountTag + encodeURI(accountKey);
			}
			if (extraKey) {
				helixProjectsAttributesUrl += AmpersandTag + extraKey;
			}
		}
		return helixProjectsAttributesUrl;
	}

	static getHelixChartFilterAttributes(helixProjectAttributes, accountKey, extraFilters, entityBU) {
		let filters = new Map();

		if (extraFilters.size > 0) {
			filters = extraFilters;
		}

		if (helixProjectAttributes) {
			if (helixProjectAttributes?.currencyCode) {
				filters.set(CurrencyCodeTag.replace('=', ''), helixProjectAttributes.currencyCode);
			}
			if (helixProjectAttributes?.currencyType) {
				filters.set(CurrencyTypeTag.replace('=', ''), helixProjectAttributes.currencyType);
			}
			if (helixProjectAttributes?.analysisPeriod?.yearToDateQuery) {
				filters.set(AnalysisYearToDateTag.replace('=', ''), helixProjectAttributes.analysisPeriod.yearToDateQuery);
			}
			if (helixProjectAttributes?.comparativePeriod?.yearToDateQuery) {
				filters.set(
					ComparativeYearToDateTag.replace('=', ''),
					helixProjectAttributes.comparativePeriod?.yearToDateQuery
				);
			}
			if (
				(helixProjectAttributes?.isAllBusinessUnitSelected === false && helixProjectAttributes?.businessUnit) ||
				entityBU
			) {
				const businessUnits = entityBU || helixProjectAttributes.businessUnit;
				let BUs = [];
				businessUnits.forEach((bu) => {
					BUs.push(bu);
				});
				if (BUs.length > 0) {
					filters.set(BUTag, BUs);
				}
			}
			if (accountKey) {
				filters.set(EYAccountTag.replace('=', ''), accountKey);
			}
		}

		return filters;
	}

	static getHelixMappingModuleUrl(helixProject, engagementId, engagementName) {
		const helixProjectAttributesUrl = this.getUrlFromHelixAttributes(
			helixProject?.helixProjectAttributesReadModel || helixProject?.helixJsonProjectAttributes
		);
		let helixMappingModuleUrl = `${HELIX_PROJECT_FIT}${helixProject?.projectId}/${
			helixProject?.helixAnalyzerId
		}/${engagementId}/${encodeURIComponent(engagementName)}${helixProjectAttributesUrl}`;
		console.log('helixMappingModuleUrl: ' + helixMappingModuleUrl);
		return helixMappingModuleUrl;
	}

	static getUrlFromHelixAttributes(helixProjectAttributes) {
		let helixProjectsAttributesUrl = '';
		if (helixProjectAttributes) {
			helixProjectsAttributesUrl = `/${helixProjectAttributes.currencyType}/${helixProjectAttributes.currencyCode}?AnsYr=${helixProjectAttributes.analysisPeriod?.yearToDateQuery}~&~ComYr=${helixProjectAttributes.comparativePeriod?.yearToDateQuery}`;
			if (helixProjectAttributes.isAllBusinessUnitSelected === false && helixProjectAttributes?.businessUnit) {
				let businessUnit = '';
				if (helixProjectAttributes?.businessUnit && helixProjectAttributes?.businessUnit.length > 0) {
					businessUnit += '~&~BU=';
					helixProjectAttributes?.businessUnit.forEach((bu) => {
						businessUnit += bu + '~|~';
					});
				}
				if (businessUnit.length > 0 && businessUnit.charAt(businessUnit.length - 1) === '~') {
					businessUnit = businessUnit.substring(0, businessUnit.length - 3);
				}
				helixProjectsAttributesUrl += businessUnit;
			}
		}
		return helixProjectsAttributesUrl;
	}

	static getAresUrl(configuration, engagementId, documentId, taskId, urlParameters) {
		if (configuration?.features && engagementId) {
			let url = configuration.features.canvasGuidedWorkflowBase;

			if (documentId) {
				// Activity page url
				url += url.endsWith('/') ? `${documentId}` : `/${documentId}`;
			}

			url += `?engagementId=${engagementId}`;
			if (taskId) {
				url += `&taskId=${taskId}`;
			}

			if (urlParameters) {
				Object.entries(urlParameters).forEach((urlParam) => {
					let [paramName, paramValue] = urlParam;
					url += `&${paramName}=${paramValue}`;
				});
			}

			url += `&referrer=${encodeURI(window.location.href)}`;

			return url;
		}
	}

	/**
	 * DocHelperConfig is being updated to support Multiple Users
	 * Below is a ping call to check if docHelper API supports multiple users
	 * In case of failure it will fallback to exisiting api
	 * TODO: Remove the static method and update the config in take a parameter for user
	 * DO NOT use this method except for config
	 * All other data has to go through redux store.
	 */
	static docHelperConfig = envConfig.docHelper;

	static setDocHelperConfig = (u) => {
		const user = u.replace('@', '.');
		const updateConfig = (usr) => {
			this.docHelperConfig = {
				...this.docHelperConfig,
				docHelperApiUri: this.docHelperConfig.docHelperApiUri.replace(':58001', `:58001/${usr}`),
				SignalRHubUrl: this.docHelperConfig.SignalRHubUrl.replace(':58002', `:58002/${usr}`)
			};
		};
		const defaultDocHelperPing = axios({
			method: 'GET',
			url: `${this.docHelperConfig.docHelperApiUri}onlinestate`
		});

		let docHelperApi = {...this.docHelperConfig}.docHelperApiUri;
		docHelperApi = docHelperApi.replace(':58001', `:58001/${user}`);
		const userDocHelperPing = axios({
			method: 'GET',
			url: `${docHelperApi}onlinestate`
		});

		/**
		 * Result 1: default succeeded first - No change to config required
		 * Result 2: upn succeeded first - Update config with user upn
		 * Result 3: default failed first - Update config with user upn
		 * Result 4: upn failed first - No change to config required
		 */
		return Promise.race([defaultDocHelperPing, userDocHelperPing])
			.then((r) => {
				let config_url = r.config.url;
				if (config_url.includes(user)) {
					updateConfig(user);
				}
			})
			.catch((e) => {
				let config_url = e.config.url;
				if (!config_url.includes(user)) {
					updateConfig(user);
				}
			});
	};

	/**
	 * new method to get DochelperConfig. Application/feature code has to be use this method instead of _docHelperConfig
	 * _docHelper is a webpack define/injected variable build will replace it all over the code
	 */
	static getDocHelperConfig() {
		return this.docHelperConfig;
	}

	static getUniversalUrl(path = '') {
		return `${envConfig.url.universal_web}${path}`;
	}

	static getAtlasBaseUrl(path = '') {
		return `${envConfig.url.atlasBaseUrl}${path}`;
	}

	static getDocServiceUrl(path = '') {
		let url = envConfig.url.Engg_Doc_Url;

		if (url.endsWith('/')) {
			url = url.slice(0, -1);
		}

		if (path) {
			url += `/${path}`;
		}

		return url;
	}

	static getURL(urlName) {
		return envConfig.url[urlName];
	}

	static getInstrumentationKey() {
		return envConfig.ApplicationInsightsConfiguration.instrumentationkey;
	}

	/**
	 * Gets the base url for Hercules API calls.
	 * @param {String} urlName - urlName
	 * @param engagementId - engagementId
	 * @return {String} the base URL, with the engagementId included in the url.
	 */
	static getBaseURL(urlName, engagementId) {
		let apiUrl = this.getURL(urlName);
		if (!apiUrl) {
			return '';
		}
		if (!apiUrl.endsWith('/')) {
			apiUrl += '/';
		}

		if (engagementId) {
			apiUrl = `${apiUrl}Engagements/${engagementId}/`;
		}

		return apiUrl;
	}

	static getSmartAutomationUrl(path = '') {
		return `${_.trimEnd(envConfig.url.Smart_Automation_Url, '/')}${path}`;
	}

	/**
	 * Get the Language from the package.json file
	 * @return {String} the language defined in the package file
	 */
	static getLanguage() {
		return envConfig.language;
	}

	/**
	 * Gets the correct error message to be displayed
	 * @param {Object} error - the error
	 * @returns {String} the correct message
	 * */
	static getErrorMessage(error) {
		if (error.response) {
			if (error.response.data === '') {
				return error.response.statusText;
			} else if (error.response.data.errorMessage) {
				return error.response.data.errorMessage;
			}
		} else if (error.message && error.message !== '') {
			return error.message;
		} else {
			return error.response.message;
		}
	}

	/**
	 * renders text for tooltip. We may want to remove html tags later.
	 * @param {string} text - the text to cleanup
	 * @returns {string} the cleaned up text
	 * */
	static renderTextForTooltip(text) {
		return text;
	}
	static allowDrydocks() {
		// Don't mind this expression.
		// It's just trying to work for string values in paas.json
		// and/or boolean values in development.json
		return envConfig.enableDrydocks === 'true' || envConfig.enableDrydocks === true;
	}
}

export default Env;
